import { type FormEvent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth";

export function SettingsPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { user, checkSession, signOut, workspaceName } = useAuth();

	const [name, setName] = useState(user?.name ?? "");
	const [nameLoading, setNameLoading] = useState(false);
	const [nameError, setNameError] = useState("");
	const [nameSuccess, setNameSuccess] = useState("");

	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [pwLoading, setPwLoading] = useState(false);
	const [pwError, setPwError] = useState("");
	const [pwSuccess, setPwSuccess] = useState("");

	const [newEmail, setNewEmail] = useState("");
	const [emailLoading, setEmailLoading] = useState(false);
	const [emailError, setEmailError] = useState("");
	const [emailSuccess, setEmailSuccess] = useState("");

	const [picUrl, setPicUrl] = useState(user?.image ?? "");
	const [picLoading, setPicLoading] = useState(false);
	const [picError, setPicError] = useState("");
	const [picSuccess, setPicSuccess] = useState("");
	const [picPreviewStatus, setPicPreviewStatus] = useState<
		"idle" | "loading" | "loaded" | "error"
	>(user?.image ? "loaded" : "idle");

	const [wsName, setWsName] = useState(workspaceName ?? "");
	const [wsNameLoading, setWsNameLoading] = useState(false);
	const [wsNameError, setWsNameError] = useState("");
	const [wsNameSuccess, setWsNameSuccess] = useState("");

	const [deleteConfirmText, setDeleteConfirmText] = useState("");
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteError, setDeleteError] = useState("");
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	async function handleNameSubmit(e: FormEvent) {
		e.preventDefault();
		setNameError("");
		setNameSuccess("");

		const trimmed = name.trim();
		if (!trimmed) {
			setNameError(t("settings.profile.nameRequired"));
			return;
		}

		setNameLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/profile/name`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ name: trimmed }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setNameError(
					(body as { error?: string } | null)?.error ??
						t("errors.updateFailed", { status: res.status }),
				);
				return;
			}
			await checkSession();
			setNameSuccess(t("settings.profile.updateSuccess"));
		} catch {
			setNameError(t("errors.networkError"));
		} finally {
			setNameLoading(false);
		}
	}

	async function handleEmailSubmit(e: FormEvent) {
		e.preventDefault();
		setEmailError("");
		setEmailSuccess("");

		const trimmed = newEmail.trim();
		if (!trimmed) {
			setEmailError(t("settings.email.emailRequired"));
			return;
		}

		setEmailLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/profile/email`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ newEmail: trimmed }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setEmailError(
					(body as { error?: string } | null)?.error ??
						t("errors.updateFailed", { status: res.status }),
				);
				return;
			}
			await checkSession();
			setNewEmail("");
			setEmailSuccess(t("settings.email.verificationSent"));
		} catch {
			setEmailError(t("errors.networkError"));
		} finally {
			setEmailLoading(false);
		}
	}

	async function handlePasswordSubmit(e: FormEvent) {
		e.preventDefault();
		setPwError("");
		setPwSuccess("");

		if (!currentPassword || !newPassword || !confirmPassword) {
			setPwError(t("settings.password.allFieldsRequired"));
			return;
		}
		if (newPassword.length < 8 || newPassword.length > 128) {
			setPwError(t("settings.password.lengthError"));
			return;
		}
		if (newPassword !== confirmPassword) {
			setPwError(t("settings.password.mismatch"));
			return;
		}

		setPwLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/profile/password`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ currentPassword, newPassword }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setPwError(
					(body as { error?: string } | null)?.error ??
						t("errors.updateFailed", { status: res.status }),
				);
				return;
			}
			await checkSession();
			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
			setPwSuccess(t("settings.password.updateSuccess"));
		} catch {
			setPwError(t("errors.networkError"));
		} finally {
			setPwLoading(false);
		}
	}

	async function handleImageSubmit(e: FormEvent) {
		e.preventDefault();
		setPicError("");
		setPicSuccess("");

		const imageValue = picUrl.trim() || null;

		setPicLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/profile/image`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ image: imageValue }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setPicError(
					(body as { error?: string } | null)?.error ??
						t("errors.updateFailed", { status: res.status }),
				);
				return;
			}
			await checkSession();
			setPicSuccess(t("settings.profilePicture.updateSuccess"));
		} catch {
			setPicError(t("errors.networkError"));
		} finally {
			setPicLoading(false);
		}
	}

	async function handleWsNameSubmit(e: FormEvent) {
		e.preventDefault();
		setWsNameError("");
		setWsNameSuccess("");

		const trimmed = wsName.trim();
		if (!trimmed) {
			setWsNameError(t("settings.workspace.nameRequired"));
			return;
		}

		setWsNameLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/settings`, {
				method: "PATCH",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ name: trimmed }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setWsNameError(
					(body as { error?: string } | null)?.error ??
						t("errors.updateFailed", { status: res.status }),
				);
				return;
			}
			await checkSession();
			setWsNameSuccess(t("settings.workspace.updateSuccess"));
		} catch {
			setWsNameError(t("errors.networkError"));
		} finally {
			setWsNameLoading(false);
		}
	}

	async function handleDeleteAccount() {
		setDeleteError("");
		setDeleteLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/account`, {
				method: "DELETE",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setDeleteError(
					(body as { error?: string } | null)?.error ??
						t("settings.deleteAccount.deletionFailed", { status: res.status }),
				);
				return;
			}
			await signOut();
			navigate(`/${workspaceSlug}/sign-in`, { replace: true });
		} catch {
			setDeleteError(t("errors.networkError"));
		} finally {
			setDeleteLoading(false);
		}
	}

	return (
		<div className="max-w-2xl mx-auto space-y-6">
			<h1 className="text-2xl font-bold text-foreground">
				{t("settings.heading")}
			</h1>

			{/* Workspace Name */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						{t("settings.workspace.title")}
					</CardTitle>
					<CardDescription>
						{t("settings.workspace.description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleWsNameSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="workspace-name">
								{t("settings.workspace.nameLabel")}
							</Label>
							<Input
								id="workspace-name"
								value={wsName}
								onChange={(e) => {
									setWsName(e.target.value);
									setWsNameSuccess("");
									setWsNameError("");
								}}
								maxLength={100}
							/>
						</div>
						{wsNameError && (
							<p className="text-sm text-destructive">{wsNameError}</p>
						)}
						{wsNameSuccess && (
							<p className="text-sm text-green-600">{wsNameSuccess}</p>
						)}
						<Button type="submit" disabled={wsNameLoading}>
							{wsNameLoading
								? t("settings.workspace.saving")
								: t("settings.workspace.save")}
						</Button>
					</form>
				</CardContent>
			</Card>

			{/* Profile Info */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						{t("settings.profile.title")}
					</CardTitle>
					<CardDescription>{t("settings.profile.description")}</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleNameSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="display-name">
								{t("settings.profile.nameLabel")}
							</Label>
							<Input
								id="display-name"
								value={name}
								onChange={(e) => {
									setName(e.target.value);
									setNameSuccess("");
									setNameError("");
								}}
							/>
						</div>
						{nameError && (
							<p className="text-sm text-destructive">{nameError}</p>
						)}
						{nameSuccess && (
							<p className="text-sm text-green-600">{nameSuccess}</p>
						)}
						<Button type="submit" disabled={nameLoading}>
							{nameLoading
								? t("settings.profile.saving")
								: t("settings.profile.save")}
						</Button>
					</form>
				</CardContent>
			</Card>

			{/* Email */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">{t("settings.email.title")}</CardTitle>
					<CardDescription>{t("settings.email.description")}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>{t("settings.email.currentLabel")}</Label>
							<div className="flex items-center gap-2">
								<p className="text-sm text-muted-foreground">{user?.email}</p>
								{user?.emailVerified ? (
									<span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
										{t("settings.email.verified")}
									</span>
								) : (
									<span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
										{t("settings.email.unverified")}
									</span>
								)}
							</div>
						</div>
						<form onSubmit={handleEmailSubmit} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="new-email">
									{t("settings.email.newLabel")}
								</Label>
								<Input
									id="new-email"
									type="email"
									value={newEmail}
									onChange={(e) => {
										setNewEmail(e.target.value);
										setEmailError("");
										setEmailSuccess("");
									}}
								/>
							</div>
							{emailError && (
								<p className="text-sm text-destructive">{emailError}</p>
							)}
							{emailSuccess && (
								<p className="text-sm text-green-600">{emailSuccess}</p>
							)}
							<Button type="submit" disabled={emailLoading}>
								{emailLoading
									? t("settings.email.sending")
									: t("settings.email.sendVerification")}
							</Button>
						</form>
					</div>
				</CardContent>
			</Card>

			{/* Password */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						{t("settings.password.title")}
					</CardTitle>
					<CardDescription>
						{t("settings.password.description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handlePasswordSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="current-password">
								{t("settings.password.currentLabel")}
							</Label>
							<Input
								id="current-password"
								type="password"
								value={currentPassword}
								onChange={(e) => {
									setCurrentPassword(e.target.value);
									setPwError("");
									setPwSuccess("");
								}}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="new-password">
								{t("settings.password.newLabel")}
							</Label>
							<Input
								id="new-password"
								type="password"
								value={newPassword}
								onChange={(e) => {
									setNewPassword(e.target.value);
									setPwError("");
									setPwSuccess("");
								}}
								minLength={8}
								maxLength={128}
							/>
							<p className="text-xs text-muted-foreground">
								{t("settings.password.hint")}
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="confirm-password">
								{t("settings.password.confirmLabel")}
							</Label>
							<Input
								id="confirm-password"
								type="password"
								value={confirmPassword}
								onChange={(e) => {
									setConfirmPassword(e.target.value);
									setPwError("");
									setPwSuccess("");
								}}
								minLength={8}
								maxLength={128}
							/>
						</div>
						{pwError && <p className="text-sm text-destructive">{pwError}</p>}
						{pwSuccess && <p className="text-sm text-green-600">{pwSuccess}</p>}
						<Button type="submit" disabled={pwLoading}>
							{pwLoading
								? t("settings.password.saving")
								: t("settings.password.changePassword")}
						</Button>
					</form>
				</CardContent>
			</Card>

			{/* Profile Picture */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						{t("settings.profilePicture.title")}
					</CardTitle>
					<CardDescription>
						{t("settings.profilePicture.description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleImageSubmit} className="space-y-4">
						<div className="flex items-start gap-4">
							{picPreviewStatus === "loaded" && picUrl.trim() ? (
								<img
									src={picUrl.trim()}
									alt="Profile preview"
									className="w-20 h-20 rounded-full object-cover"
									onError={() => setPicPreviewStatus("error")}
								/>
							) : (
								<div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center text-2xl font-semibold text-muted-foreground">
									{user?.name?.charAt(0)?.toUpperCase() ?? "?"}
								</div>
							)}
							<div className="flex-1 space-y-2">
								<Label htmlFor="profile-image-url">
									{t("settings.profilePicture.urlLabel")}
								</Label>
								<Input
									id="profile-image-url"
									type="url"
									placeholder="https://example.com/avatar.jpg"
									value={picUrl}
									onChange={(e) => {
										setPicUrl(e.target.value);
										setPicError("");
										setPicSuccess("");
										setPicPreviewStatus("idle");
									}}
									onBlur={() => {
										const url = picUrl.trim();
										if (!url) {
											setPicPreviewStatus("idle");
											return;
										}
										try {
											new URL(url);
											setPicPreviewStatus("loading");
											const img = new Image();
											img.onload = () => setPicPreviewStatus("loaded");
											img.onerror = () => setPicPreviewStatus("error");
											img.src = url;
										} catch {
											setPicPreviewStatus("error");
										}
									}}
								/>
								{picPreviewStatus === "loading" && (
									<p className="text-sm text-muted-foreground">
										{t("settings.profilePicture.loadingPreview")}
									</p>
								)}
								{picPreviewStatus === "error" && (
									<p className="text-sm text-destructive">
										{t("settings.profilePicture.loadError")}
									</p>
								)}
							</div>
						</div>
						{picError && <p className="text-sm text-destructive">{picError}</p>}
						{picSuccess && (
							<p className="text-sm text-green-600">{picSuccess}</p>
						)}
						<Button
							type="submit"
							disabled={
								picLoading ||
								(picUrl.trim() !== "" && picPreviewStatus !== "loaded")
							}
						>
							{picLoading
								? t("settings.profilePicture.saving")
								: t("settings.profilePicture.save")}
						</Button>
					</form>
				</CardContent>
			</Card>

			{/* API Documentation */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						{t("settings.apiDocs.title")}
					</CardTitle>
					<CardDescription>{t("settings.apiDocs.description")}</CardDescription>
				</CardHeader>
				<CardContent>
					<a href="/api/docs" target="_blank" rel="noopener noreferrer">
						<Button variant="outline">{t("settings.apiDocs.openDocs")}</Button>
					</a>
				</CardContent>
			</Card>

			{/* Delete Account */}
			<Card className="border-destructive">
				<CardHeader>
					<CardTitle className="text-lg text-destructive">
						{t("settings.deleteAccount.title")}
					</CardTitle>
					<CardDescription>
						{t("settings.deleteAccount.description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Dialog
						open={deleteDialogOpen}
						onOpenChange={(open) => {
							setDeleteDialogOpen(open);
							if (!open) {
								setDeleteConfirmText("");
								setDeleteError("");
							}
						}}
					>
						<DialogTrigger asChild>
							<Button variant="destructive">
								{t("settings.deleteAccount.button")}
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>
									{t("settings.deleteAccount.dialogTitle")}
								</DialogTitle>
								<DialogDescription>
									<Trans
										i18nKey="settings.deleteAccount.dialogDescription"
										components={{
											1: <span className="font-mono font-bold" />,
										}}
									/>
								</DialogDescription>
							</DialogHeader>
							<div className="space-y-4">
								<Input
									placeholder={t("settings.deleteAccount.confirmPlaceholder")}
									value={deleteConfirmText}
									onChange={(e) => {
										setDeleteConfirmText(e.target.value);
										setDeleteError("");
									}}
								/>
								{deleteError && (
									<p className="text-sm text-destructive">{deleteError}</p>
								)}
							</div>
							<DialogFooter>
								<Button
									variant="destructive"
									disabled={deleteConfirmText !== "DELETE" || deleteLoading}
									onClick={handleDeleteAccount}
								>
									{deleteLoading
										? t("settings.deleteAccount.deleting")
										: t("settings.deleteAccount.confirmDelete")}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</CardContent>
			</Card>
		</div>
	);
}
